export type ResolveFixtureListLinkUrlParams = {
  competitionId?: string;
  clubId?: string;
  clubFromSlug?: boolean;
  periodLimit?: number;
  linkUrl?: string;
};

/**
 * Derives the Fixture List link URL from the selected competition and period.
 *
 * Only applies when a competition is selected AND no club is selected
 * (neither an explicit clubId nor clubFromSlug). Positive periodLimit means
 * upcoming fixtures (/fixtures); anything else (0, negative, or a Custom range
 * where periodLimit is undefined) means past results (/results).
 *
 * Falls back to the admin-provided linkUrl for all other cases.
 *
 * Ids are normally Mongo ObjectIds (no special characters), but each id is
 * URL-encoded defensively so any stray whitespace/character can never produce
 * a malformed query string; the comma separator itself is left literal.
 */
export function resolveFixtureListLinkUrl({
  competitionId,
  clubId,
  clubFromSlug,
  periodLimit,
  linkUrl,
}: ResolveFixtureListLinkUrlParams): string | undefined {
  const competitionIds = (competitionId ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  const clubSelected = clubFromSlug === true || !!clubId?.trim();

  if (competitionIds.length === 0 || clubSelected) {
    return linkUrl;
  }

  const path = periodLimit !== undefined && periodLimit > 0 ? '/fixtures' : '/results';
  const query = competitionIds.map((id) => encodeURIComponent(id)).join(',');
  return `${path}?competition=${query}`;
}
